import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import {
  trigger,
  style,
  animate,
  transition,
  keyframes,
  AnimationEvent,
} from '@angular/animations';
import { Subject, timer, Observable, of } from 'rxjs';
import { CurrencyAmountDisplay } from 'src/app/core/services/currency/currency-format';
import { takeUntil, catchError, finalize } from 'rxjs/operators';
import {
  MessageSegment,
  MessageSegmentType,
  StructuredHintMessage,
} from './model/structured-message';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemePipe } from '../../core/pipes/theme.pipe';
import { CurrencyIconComponent } from '../currency-icon/currency-icon.component';
import { NgStyle, NgClass } from '@angular/common';

export interface HintDisplayConfig {
  initialDelayMs: number;
  multiplier: number;
  maxDelayMs: number;
  maxVisibleDurationMs: number;
}

export interface HintCycleEvent {
  message: StructuredHintMessage;
  index: number;
  delay: number;
}

export interface HintFetchRequest {
  isInitial: boolean;
}

export type HintMessageFetcher = (request: HintFetchRequest) => Observable<StructuredHintMessage[]>;

const DefaultDisplayConfig: HintDisplayConfig = {
  initialDelayMs: 3000,
  multiplier: 2,
  maxDelayMs: 48000,
  maxVisibleDurationMs: 12000,
};

@Component({
  selector: 'app-notification-hint',
  templateUrl: './notification-hint.component.html',
  styleUrls: ['./notification-hint.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('hintAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(100%)' }),
        animate(
          '800ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          keyframes([
            style({ opacity: 0, transform: 'translateY(100%)', offset: 0 }),
            style({ opacity: 0.15, transform: 'translateY(85%)', offset: 0.15 }),
            style({ opacity: 0.35, transform: 'translateY(60%)', offset: 0.3 }),
            style({ opacity: 0.6, transform: 'translateY(30%)', offset: 0.45 }),
            style({ opacity: 0.9, transform: 'translateY(5%)', offset: 0.58 }),
            style({ opacity: 1, transform: 'translateY(-20%)', offset: 0.75 }),
            style({ opacity: 1, transform: 'translateY(0)', offset: 1 }),
          ]),
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({
            opacity: 0,
            transform: 'translateY(50%)',
          }),
        ),
      ]),
    ]),
  ],
  imports: [NgStyle, NgClass, CurrencyIconComponent, ThemePipe],
})
export class NotificationHintComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public messageFetcher!: HintMessageFetcher;

  @Output() public readonly cycleStarted = new EventEmitter<void>();
  @Output() public readonly cycleStopped = new EventEmitter<void>();
  @Output() public readonly messageShown = new EventEmitter<HintCycleEvent>();
  @Output() public readonly messageHidden = new EventEmitter<HintCycleEvent>();
  @Output() public readonly cycleCompleted = new EventEmitter<void>();
  @Output() public readonly fetchStarted = new EventEmitter<HintFetchRequest>();
  @Output() public readonly fetchCompleted = new EventEmitter<StructuredHintMessage[]>();
  @Output() public readonly fetchFailed = new EventEmitter<Error>();
  @Output() public readonly exhausted = new EventEmitter<void>();

  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);

  public currentMessage: StructuredHintMessage | null = null;
  public isLoading = false;

  private displayConfig: HintDisplayConfig = DefaultDisplayConfig;
  private messages: StructuredHintMessage[] = [];
  private currentIndex = 0;
  private currentDelay: number = DefaultDisplayConfig.initialDelayMs;
  private isCycling = false;
  private isInitialFetch = true;
  private remainingDelayMs = 0;
  private readonly destroy$ = new Subject<void>();
  private readonly stopCycle$ = new Subject<void>();

  public readonly hintStyles: [string, string][] = [
    ['type', 'notification-hint-text'],
    ['text-color', 'notification-hint-text'],
  ];

  public ngOnInit(): void {
    this.currentDelay = this.displayConfig.initialDelayMs;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCycle$.complete();
  }

  public start(): void {
    if (this.isCycling || this.isLoading) {
      return;
    }

    this.isCycling = true;
    this.currentIndex = 0;
    this.currentDelay = this.displayConfig.initialDelayMs;
    this.isInitialFetch = true;
    this.remainingDelayMs = 0;
    this.cycleStarted.emit();

    this.fetchMessages();
  }

  public stop(): void {
    if (!this.isCycling && !this.isLoading) {
      return;
    }

    this.isCycling = false;
    this.isLoading = false;
    this.remainingDelayMs = 0;
    this.stopCycle$.next();
    this.cycleStopped.emit();
    this.currentMessage = null;
    this.changeDetector.markForCheck();
  }

  public reset(): void {
    this.stop();
    this.messages = [];
    this.currentIndex = 0;
    this.currentDelay = this.displayConfig.initialDelayMs;
    this.isInitialFetch = true;
    this.remainingDelayMs = 0;
  }

  public next(): void {
    if (!this.isCycling) {
      return;
    }

    this.stopCycle$.next();
    this.remainingDelayMs = 0;
    this.hideCurrentMessage();
  }

  public get isActive(): boolean {
    return this.isCycling;
  }

  public get hasMessages(): boolean {
    return this.messages.length > 0;
  }

  public onAnimationDone(event: AnimationEvent): void {
    if (event.fromState === 'void') {
      // Enter animation completed - message is now fully visible
      this.messageShown.emit(this.createCycleEvent());

      if (this.isCycling) {
        this.scheduleMessageHide();
      }
      return;
    }

    if (event.toState === 'void') {
      // Leave animation completed - message is now fully hidden
      this.messageHidden.emit(this.createCycleEvent());

      if (this.isCycling) {
        this.scheduleNextMessage();
      }
    }
  }

  public get currentMessageText(): string {
    if (!this.currentMessage) {
      return '';
    }

    return this.currentMessage.segments.map((s) => s.content).join('');
  }

  public getSegmentClass(segment: MessageSegment): string {
    return `hint-segment hint-segment--${segment.type}`;
  }

  public static createStructuredMessage(
    id: string,
    segments: Array<[MessageSegmentType, string | CurrencyAmountDisplay]>,
  ): StructuredHintMessage {
    return {
      id,
      segments: segments.map(([type, value]) =>
        typeof value === 'string'
          ? { type, content: value }
          : { type, content: value.text, icon: value.icon, iconPosition: value.iconPosition },
      ),
    };
  }

  private fetchMessages(): void {
    if (this.isLoading || !this.isCycling) {
      return;
    }

    const request: HintFetchRequest = {
      isInitial: this.isInitialFetch,
    };

    this.isLoading = true;
    this.fetchStarted.emit(request);
    this.changeDetector.markForCheck();

    this.messageFetcher(request)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.stopCycle$),
        catchError((error: Error) => {
          this.fetchFailed.emit(error);
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.isInitialFetch = false;
          this.changeDetector.markForCheck();
        }),
      )
      .subscribe((newMessages) => {
        this.handleFetchedMessages(newMessages);
      });
  }

  private handleFetchedMessages(newMessages: StructuredHintMessage[]): void {
    this.fetchCompleted.emit(newMessages);

    if (newMessages.length === 0) {
      this.exhausted.emit();

      if (this.messages.length === 0) {
        this.isCycling = false;
        this.cycleStopped.emit();
      }
      return;
    }

    this.messages = newMessages;
    this.currentIndex = 0;

    if (this.isCycling && !this.currentMessage) {
      this.showCurrentMessage();
      this.changeDetector.markForCheck();
    }
  }

  private showCurrentMessage(): void {
    if (!this.isCycling || this.messages.length === 0) {
      return;
    }

    this.currentMessage = this.messages[this.currentIndex];
  }

  private hideCurrentMessage(): void {
    this.currentMessage = null;
    this.changeDetector.markForCheck();
  }

  private scheduleMessageHide(): void {
    const visibleDuration = Math.min(this.currentDelay, this.displayConfig.maxVisibleDurationMs);

    // Calculate how much time remains after the message hides
    this.remainingDelayMs = Math.max(0, this.currentDelay - visibleDuration);

    timer(visibleDuration)
      .pipe(takeUntil(this.destroy$), takeUntil(this.stopCycle$))
      .subscribe(() => {
        if (this.isCycling && this.currentMessage) {
          this.hideCurrentMessage();
        }
      });
  }

  private scheduleNextMessage(): void {
    const delayBeforeNext = this.remainingDelayMs;
    this.remainingDelayMs = 0;

    timer(delayBeforeNext)
      .pipe(takeUntil(this.destroy$), takeUntil(this.stopCycle$))
      .subscribe(() => {
        if (!this.isCycling) {
          return;
        }

        this.advanceToNextMessage();

        // Check if we've completed a full cycle through current messages
        if (this.currentIndex === 0) {
          this.cycleCompleted.emit();
          this.fetchMessages();
          return;
        }

        this.showCurrentMessage();
        this.changeDetector.markForCheck();
      });
  }

  private advanceToNextMessage(): void {
    this.currentIndex = (this.currentIndex + 1) % this.messages.length;

    this.currentDelay = Math.min(
      this.currentDelay * this.displayConfig.multiplier,
      this.displayConfig.maxDelayMs,
    );
  }

  private createCycleEvent(): HintCycleEvent {
    return {
      message: this.messages[this.currentIndex],
      index: this.currentIndex,
      delay: this.currentDelay,
    };
  }
}
